import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { ChevronsUpDown, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { mlsOrganizations } from "@/data/mlsOrganizations";

interface MlsComboboxProps {
  value: string;
  onChange: (mls: string) => void;
  placeholder?: string;
}

// Shared MLS picker — extracted verbatim from BasicInfoSection so the profile
// and the onboarding wizard share one control and round-trip the same canonical
// MLS organization name. Emits that name via onChange; callers map it to their
// own keys.
export function MlsCombobox({ value, onChange, placeholder = "Search MLS..." }: MlsComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal text-left truncate">
          <span className="truncate">{value || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command>
          <CommandInput placeholder="Type to search MLS..." value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>
              <div className="p-2 text-sm">
                <p className="text-muted-foreground mb-2">No match found.</p>
                {search && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      onChange(search);
                      setOpen(false);
                      setSearch("");
                    }}
                  >
                    Use "{search}"
                  </Button>
                )}
              </div>
            </CommandEmpty>
            <CommandGroup>
              {mlsOrganizations.map((mls) => (
                <CommandItem key={mls} value={mls} onSelect={() => {
                  onChange(mls);
                  setOpen(false);
                  setSearch("");
                }}>
                  <Check className={cn("mr-2 h-4 w-4", value === mls ? "opacity-100" : "opacity-0")} />
                  <span className="truncate">{mls}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
